/**
 * Mug colour overlay — user-initiated activation, with size-specific hero
 * swaps for 12oz Latte and Travel Mug.
 *
 * SIZE MODES
 *   Standard (10oz, 11oz, 15oz, 20oz):
 *     - Hero: ceramic photo (default product image)
 *     - Overlay: colour-keyed (red, blue, etc.)
 *   12oz Latte:
 *     - Hero: latte-warped composition ({handle}.webp in Shopify Files)
 *     - Overlay: none (composition is complete)
 *   Travel Mug:
 *     - Hero: travel-mug-warped composition (travelmug-{handle}.webp)
 *     - Overlay: white lid only when Colour = White; otherwise none
 *       (photographed default lid is black).
 *
 * INTEGRATIONS
 *   A. Standard variant-picker via `on:variant:change`.
 *   B. Sipfull custom picker via delegated click on all picker groups.
 *
 * The overlay <img> starts hidden (display:none from snippet). User
 * interaction triggers all updates — no initial fetches on page load.
 */
(function () {
  'use strict';

  // Loud version marker — bump the date string whenever you ship a real
  // change you want to verify is live in the browser (and not stuck in
  // a CDN / cache layer somewhere). The console log makes it instant
  // to confirm a deploy actually landed: open DevTools → Console on a
  // product page and look for the orange-highlighted line.
  var MUG_OVERLAY_VERSION = '2026-05-19-variant-primary-v2';
  console.log(
    '%c[mug-overlay] loaded ' + MUG_OVERLAY_VERSION,
    'background:#c75b12;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;'
  );
  window.__MUG_OVERLAY_VERSION = MUG_OVERLAY_VERSION;

  // No more skipping — both Latte and Travel Mug have working previews now.
  var SKIP_PEEK_SIZES = [];

  function parseUrls(overlay) {
    try {
      return JSON.parse(overlay.dataset.urls || '{}');
    } catch (e) {
      console.warn('[mug-color-overlay] invalid data-urls JSON', e);
      return {};
    }
  }

  function isLatte(size)     { return size === '12oz Latte'; }
  function isTravelMug(size) { return size === 'Travel Mug'; }

  /**
   * Pick the overlay URL appropriate to the current state.
   *
   *   - Latte: never has an overlay (the composite image is complete).
   *   - Travel Mug: no overlay for coloured variants (ceramic-shape
   *     colour PNGs don't fit the travel mug silhouette), BUT paint the
   *     white-lid overlay when Colour = White. The photographed base
   *     travel mug has a black lid; white-lid customers need to see
   *     their choice reflected. Restored 2026-07-17 after the Rolling
   *     Art PDP surfaced the missing feedback. The white-lid overlay
   *     URL comes from data-travelmug-white-overlay-url on the mug-
   *     overlay element (emitted for all mug-type products).
   *   - Other sizes: standard colour-keyed map.
   */
  function pickOverlayUrl(overlay, colour, size) {
    if (isLatte(size)) return null;
    if (isTravelMug(size)) {
      if (colour === 'White' && overlay.dataset.travelmugWhiteOverlayUrl) {
        return overlay.dataset.travelmugWhiteOverlayUrl;
      }
      return null;
    }
    if (!colour) return null;
    var urls = parseUrls(overlay);
    return urls[colour] || null;
  }

  /**
   * Pick the hero src URL for the size. Returns null for standard sizes
   * (caller should restore the ceramic hero in that case).
   */
  function pickHeroUrlForSize(overlay, size) {
    if (isLatte(size))     return overlay.dataset.latteHeroUrl || null;
    if (isTravelMug(size)) return overlay.dataset.travelmugHeroUrl || null;
    return null;
  }

  function activateOverlay(overlay, url) {
    if (!overlay || !url) return;
    // Same URL → nothing to load. Just ensure it's marked active +
    // visible. Cheap idempotent path.
    if (overlay.getAttribute('src') === url) {
      overlay.setAttribute('data-activated', '');
      overlay.style.display = '';
      return;
    }
    // Hold-the-old-overlay swap. Customer feedback Jun 2026: even
    // with init-time preloading, clicking between colours flashed
    // the bare-mug handle briefly. Root cause: setting <img>.src
    // can cause a moment where the img has no rendered content
    // while the browser decodes the new image. Fix: load the new
    // URL via `new Image()` first; only swap the visible overlay's
    // src AFTER the load event fires. Means the customer sees the
    // PREVIOUS colour for a tiny instant longer (rather than the
    // bare mug) — much smoother visually.
    //
    // Race guard: if activateOverlay is called again before the
    // preload completes (rapid clicking), the LAST call wins. The
    // earlier preload's onload may still fire — it checks the
    // pending URL against the overlay's data-pending-url so a stale
    // load doesn't overwrite a newer one.
    overlay.setAttribute('data-pending-url', url);
    var preload = new Image();
    preload.onload = function () {
      // Stale-load guard: another activateOverlay() may have fired
      // since we started this preload. Only commit if our URL is
      // still the most recent one requested.
      if (overlay.getAttribute('data-pending-url') !== url) return;
      overlay.setAttribute('src', url);
      overlay.setAttribute('data-activated', '');
      overlay.style.display = '';
    };
    preload.onerror = function () {
      // Failed load — don't leave the customer staring at a stale
      // overlay forever. Force the swap so at worst they see a
      // broken icon and the system recovers on next click. Same
      // stale-load guard as the success path.
      if (overlay.getAttribute('data-pending-url') !== url) return;
      overlay.setAttribute('src', url);
      overlay.setAttribute('data-activated', '');
      overlay.style.display = '';
    };
    preload.src = url;
  }

  function hideOverlay(overlay) {
    overlay.style.display = 'none';
  }

  /**
   * Preload every colour overlay URL referenced by an overlay element.
   *
   * Customer feedback Jun 2026: when clicking between colour thumbs the
   * customer briefly saw the un-overlaid mug (the "original colour
   * handle") between each change. Root cause: setting <img>.src on the
   * overlay element kicks off a fresh network request — between request
   * start and image render, the browser shows nothing for that img, so
   * the bare-mug photo behind it became visible.
   *
   * Fix: warm the browser cache for every colour overlay URL at page
   * init via `new Image()`. Cached requests resolve synchronously when
   * the overlay's src is later swapped, so the transition is instant.
   *
   * Tracked with a global Set so repeat init calls (the overlay system
   * runs on both Shopify variant:change AND sipfull picker integrations)
   * don't double-fetch.
   */
  var _preloadedOverlayUrls = (typeof Set !== 'undefined') ? new Set() : null;
  function preloadOverlayUrls(overlay) {
    if (!overlay) return;
    var urls = parseUrls(overlay);
    var values = [];
    Object.keys(urls).forEach(function (k) {
      if (urls[k]) values.push(urls[k]);
    });
    // Travel-mug white-lid overlay isn't in the data-urls map; it's on
    // its own data attribute. Worth preloading too — the customer's
    // path is often: tap Travel Mug → tap White → flash if not cached.
    if (overlay.dataset.travelmugWhiteOverlayUrl) {
      values.push(overlay.dataset.travelmugWhiteOverlayUrl);
    }
    values.forEach(function (url) {
      if (_preloadedOverlayUrls && _preloadedOverlayUrls.has(url)) return;
      if (_preloadedOverlayUrls) _preloadedOverlayUrls.add(url);
      var img = new Image();
      img.decoding = 'async';
      // Keep priority low — page-critical assets shouldn't fight for
      // bandwidth. fetchpriority="low" tells the browser these can
      // wait for spare capacity.
      try { img.fetchPriority = 'low'; } catch (_) {}
      img.src = url;
    });
  }

  function findColourInVariant(variant, urls) {
    if (!variant || !urls) return null;
    for (var i = 1; i <= 3; i++) {
      var value = variant['option' + i];
      if (value && Object.prototype.hasOwnProperty.call(urls, value)) {
        return value;
      }
    }
    return null;
  }

  var KNOWN_SIZES = ['10oz', '11oz', '12oz Latte', '15oz', '20oz', 'Travel Mug'];
  function findSizeInVariant(variant) {
    if (!variant) return null;
    for (var i = 1; i <= 3; i++) {
      var value = variant['option' + i];
      if (value && KNOWN_SIZES.indexOf(value) >= 0) return value;
    }
    return null;
  }

  /**
   * Compare against the raw `src` attribute (via getAttribute) rather than
   * the `.src` property — the property returns the resolved absolute URL,
   * but attributes can be protocol-relative or absolute. Property-based
   * comparison would think they're always different and create a mutation
   * observer loop.
   */
  function setHeroSrc(hero, url) {
    if (!hero || !url) return;
    if (hero.getAttribute('src') === url) return;
    hero.setAttribute('src', url);
    hero.removeAttribute('srcset');
  }

  function restoreCeramicHero(root, hero) {
    if (!hero) return;
    var firstThumb = root.querySelector('[data-sp-thumb]');
    if (!firstThumb) return;
    var src = firstThumb.dataset.heroSrc;
    var srcset = firstThumb.dataset.heroSrcset;
    if (src) setHeroSrc(hero, src);
    if (srcset && hero.getAttribute('srcset') !== srcset) {
      hero.setAttribute('srcset', srcset);
    }
  }

  // ---- mobile peek-scroll ------------------------------------------------

  var peekReturnTimer = null;
  var peekReturnTarget = null;
  var activeScrollAnim = null;

  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function cancelScrollAnim() {
    if (activeScrollAnim) {
      cancelAnimationFrame(activeScrollAnim);
      activeScrollAnim = null;
    }
  }

  function smoothScrollTo(targetY) {
    cancelScrollAnim();
    var startY = window.scrollY;
    var distance = targetY - startY;
    var absDistance = Math.abs(distance);
    var duration = Math.max(450, Math.min(900, absDistance * 1.2));
    if (absDistance < 2) return Promise.resolve();

    var start = performance.now();
    return new Promise(function (resolve) {
      function step(now) {
        var t = Math.min((now - start) / duration, 1);
        window.scrollTo(0, startY + distance * easeOutCubic(t));
        if (t < 1) {
          activeScrollAnim = requestAnimationFrame(step);
        } else {
          activeScrollAnim = null;
          resolve();
        }
      }
      activeScrollAnim = requestAnimationFrame(step);
    });
  }

  function userScrolledCancel() {
    cancelScrollAnim();
    if (peekReturnTimer) {
      clearTimeout(peekReturnTimer);
      peekReturnTimer = null;
      peekReturnTarget = null;
    }
  }
  window.addEventListener('wheel', userScrolledCancel, { passive: true });
  window.addEventListener('touchmove', userScrolledCancel, { passive: true });
  window.addEventListener('keydown', function (e) {
    if (['PageUp','PageDown','Home','End','ArrowUp','ArrowDown',' '].indexOf(e.key) >= 0) {
      userScrolledCancel();
    }
  });

  function isSkippedSize(variant, overlay) {
    if (SKIP_PEEK_SIZES.length === 0) return false;
    if (variant) {
      for (var i = 1; i <= 3; i++) {
        if (SKIP_PEEK_SIZES.indexOf(variant['option' + i]) >= 0) return true;
      }
      return false;
    }
    var root = overlay && overlay.closest('[data-sipfull-product]');
    if (!root) return false;
    var active = root.querySelector('[data-picker="size"] [data-value].active');
    if (!active) return false;
    return SKIP_PEEK_SIZES.indexOf(active.getAttribute('data-value')) >= 0;
  }

  function peekAtOverlay(overlay, variant) {
    if (!overlay) return;
    if (!window.matchMedia('(max-width: 899px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (isSkippedSize(variant, overlay)) return;

    var rect = overlay.getBoundingClientRect();
    var vh = window.innerHeight;
    var visibleHeight = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
    var isSufficientlyVisible = rect.height > 0 && visibleHeight / rect.height > 0.5;

    if (!isSufficientlyVisible) {
      if (peekReturnTarget === null) peekReturnTarget = window.scrollY;
      var elCenter = rect.top + rect.height / 2;
      var targetY = window.scrollY + elCenter - vh / 2;
      smoothScrollTo(targetY);
    }

    if (peekReturnTimer) clearTimeout(peekReturnTimer);
    peekReturnTimer = setTimeout(function () {
      var target = peekReturnTarget;
      peekReturnTarget = null;
      peekReturnTimer = null;
      if (target !== null) smoothScrollTo(target);
    }, 1400);
  }

  // ---- personalised default-overlay size swap ---------------------------
  //
  // Personalised product pages render a "default state" marketing image
  // overlay on top of the live-preview gallery until the customer uploads
  // a photo. The overlay div carries data-latte-hero-url and
  // data-travelmug-hero-url attributes for the size-specific marketing
  // images. Swap the overlay's inner <img> to those when size changes
  // so the customer sees the right shape mug (latte or travel mug)
  // matching their selection. Standard sizes restore the original src.
  // Colour feedback comes from the colour overlay that's CSS-stacked
  // above the default overlay (transparent body + opaque rim/handle).
  function syncPersonalisedDefaultOverlay(size) {
    var overlays = document.querySelectorAll('[data-fb-photo-default-overlay]');
    overlays.forEach(function (wrap) {
      var img = wrap.querySelector(':scope > img');
      if (!img) return;
      // Cache the original ceramic src once so we can restore on
      // standard sizes after a latte/travelmug detour.
      if (!wrap.dataset.originalSrc) {
        wrap.dataset.originalSrc = img.getAttribute('src') || '';
        wrap.dataset.originalSrcset = img.getAttribute('srcset') || '';
      }
      var sizeUrl = null;
      if (isLatte(size))     sizeUrl = wrap.dataset.latteHeroUrl;
      if (isTravelMug(size)) sizeUrl = wrap.dataset.travelmugHeroUrl;
      if (sizeUrl) {
        if (img.getAttribute('src') !== sizeUrl) {
          img.setAttribute('src', sizeUrl);
          img.removeAttribute('srcset');
        }
      } else {
        // Restore original ceramic marketing image.
        var origSrc = wrap.dataset.originalSrc;
        var origSrcset = wrap.dataset.originalSrcset;
        if (origSrc && img.getAttribute('src') !== origSrc) {
          img.setAttribute('src', origSrc);
        }
        if (origSrcset) img.setAttribute('srcset', origSrcset);
        else            img.removeAttribute('srcset');
      }
    });
  }

  // ---- integration A: standard variant-picker ---------------------------

  document.addEventListener('on:variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant) return;
    syncPersonalisedDefaultOverlay(findSizeInVariant(variant));

    var scope =
      event.target.closest('product-form') ||
      event.target.closest('form[action*="/cart/add"]') ||
      event.target.closest('.js-product') ||
      document;

    var size = findSizeInVariant(variant);
    var overlays = scope.querySelectorAll('[data-mug-overlay]');
    if (!overlays.length) return;

    overlays.forEach(function (overlay) {
      // Warm cache for all this overlay's colour URLs so subsequent
      // swaps don't re-hit the network and flash the bare mug.
      preloadOverlayUrls(overlay);
      var urls = parseUrls(overlay);
      var colour = findColourInVariant(variant, urls);
      var overlayUrl = pickOverlayUrl(overlay, colour, size);
      if (overlayUrl) {
        activateOverlay(overlay, overlayUrl);
      } else {
        hideOverlay(overlay);
      }
    });

    var hero = scope.querySelector('[data-sp-hero]') ||
               scope.querySelector('.slider__item.is-active .theme-img') ||
               scope.querySelector('.main-image img');
    if (hero) {
      var heroUrl = pickHeroUrlForSize(overlays[0], size);
      if (heroUrl) setHeroSrc(hero, heroUrl);
    }

    peekAtOverlay(scope.querySelector('[data-mug-overlay]'), variant);
  });

  // ---- integration B: sipfull picker ------------------------------------

  function getActivePickerValue(root, dim) {
    var active = root.querySelector('[data-picker="' + dim + '"] [data-value].active');
    return active ? active.getAttribute('data-value') : null;
  }

  function initSipfullPicker(root) {
    var pickers = root.querySelectorAll('[data-picker]');
    if (!pickers.length) return;
    var overlays = root.querySelectorAll('[data-mug-overlay]');
    if (!overlays.length) return;
    var hero = root.querySelector('[data-sp-hero]');

    // Warm the browser cache for every colour overlay so clicking
    // between thumbs / swatches doesn't flash the bare mug between
    // colour changes. Fires once per init — idempotent thanks to the
    // _preloadedOverlayUrls dedupe Set.
    overlays.forEach(preloadOverlayUrls);

    function syncFromState(opts) {
      var skipOverlay = opts && opts.skipOverlay;
      var colour = getActivePickerValue(root, 'colour');
      var size = getActivePickerValue(root, 'size');

      // Hero swap: latte / travel mug get specific files; standard sizes
      // restore the canonical ceramic photo from the first thumb's data.
      // Always runs — including on init — so a customer landing on a
      // Latte or Travel Mug variant URL sees the right pre-warped image.
      if (hero) {
        var heroUrl = pickHeroUrlForSize(overlays[0], size);
        if (heroUrl) {
          setHeroSrc(hero, heroUrl);
        } else {
          restoreCeramicHero(root, hero);
        }
      }

      // Overlay: latte never has one, travel mug only when White,
      // standard sizes use colour-keyed. SKIPPED on init for the lean
      // section — see the rAF call below for context.
      if (!skipOverlay) {
        overlays.forEach(function (overlay) {
          var url = pickOverlayUrl(overlay, colour, size);
          if (url) {
            activateOverlay(overlay, url);
          } else {
            hideOverlay(overlay);
          }
        });
      }

      // Personalised default-overlay size swap (no-op on non-personalised
      // pages — there are no [data-fb-photo-default-overlay] elements).
      syncPersonalisedDefaultOverlay(size);
    }

    pickers.forEach(function (picker) {
      picker.addEventListener('click', function (event) {
        if (!event.target.closest('[data-value]')) return;
        // rAF gives the sipfull picker's own pivot logic time to finish
        // before we read final .active values.
        requestAnimationFrame(function () {
          syncFromState();
          // NOTE 2026-07-05: peekAtOverlay REMOVED here. The sipfull
          // picker (sipfull-product.liquid) now does an explicit
          // scroll-to-top on mobile variant changes. The peek's own
          // smooth-scroll (RAF loop calling window.scrollTo(0, y)
          // each frame) actively overrode our scrollTo({top:0}) on
          // the FIRST tap — peek won every frame because it re-set
          // the browser's smooth-scroll target. On the second tap the
          // overlay was already visible (from where peek had scrolled
          // to on tap 1), peek's isSufficientlyVisible check returned
          // true, peek skipped, and our scroll-to-top ran uncontested.
          // Guy's "second-and-thereafter works, first doesn't" bug.
          // Fix: drop peek here — scroll-to-top is the deliberate
          // behaviour now. Standard theme integration (line ~402)
          // keeps peek for other product templates.
          syncPersonalisedDefaultOverlay(getActivePickerValue(root, 'size'));
        });
      });
    });

    // Prefetch latte AND travel mug heroes on first interaction so the
    // first click on either feels instant. Both are ~150-250KB; only
    // fired once per page, only on user intent (hover/touch).
    var prefetched = false;
    function prefetchSizeHeroes() {
      if (prefetched) return;
      prefetched = true;
      [overlays[0].dataset.latteHeroUrl, overlays[0].dataset.travelmugHeroUrl]
        .forEach(function (url) {
          if (!url) return;
          var img = new Image();
          try { img.fetchPriority = 'low'; } catch (e) { /* old browsers */ }
          img.src = url;
        });
    }
    pickers.forEach(function (picker) {
      picker.addEventListener('mouseenter', prefetchSizeHeroes, { once: true });
      picker.addEventListener('touchstart', prefetchSizeHeroes, { once: true, passive: true });
    });

    // On init: do the size-driven hero swap only (Latte / Travel Mug
    // pre-warped composites for default-Latte / default-Travel-Mug
    // variant URLs), and SKIP the colour-overlay activation. The
    // overlay PNGs are large enough that loading them eagerly was
    // hurting first paint on the lean section, so we defer them until
    // the customer interacts with a colour swatch (the picker click
    // handler above runs syncFromState() with no opts → overlay
    // activates as expected).
    //
    // Personalised products are unaffected — the snippet still pre-
    // renders their initial overlay src so it's visible on first paint,
    // and activateOverlay() short-circuits when the src already matches.
    //
    // Trade-off: customers landing on a coloured-default-variant URL
    // (e.g. ?variant=… for Orange seventies edition) see the bare
    // ceramic mug until they touch the picker. Acceptable in exchange
    // for the perceived speed improvement on the lean catalogue pages.
    // Guy's call, May 2026.
    requestAnimationFrame(function () {
      syncFromState({ skipOverlay: true });
    });
  }

  /**
   * Hide the hero overlay when the user swipes to a non-hero carousel image,
   * reveal again when they swipe back. Also reapplies size-specific hero
   * URLs on swipe-back if currently in latte/travelmug mode.
   */
  function initSipfullHeroSrcWatcher(root) {
    var heroImg = root.querySelector('[data-sp-hero]');
    if (!heroImg) return;
    var heroOverlays = root.querySelectorAll('.sp-gallery-hero [data-mug-overlay]');
    if (!heroOverlays.length) return;

    function isPrimaryShowing() {
      var thumbs = root.querySelectorAll('[data-sp-thumb]');
      var activeThumb = root.querySelector('[data-sp-thumb].is-active');
      if (!thumbs.length || !activeThumb) return true;
      /* The original first-thumb-only test (May 2026) predated the
         variant-thumb feature (Jun 2026). Variant thumbs ARE primary
         views — they show the same ceramic photo just with a
         different colour overlay layered on top — so the overlay
         needs to stay visible while they're active. Without this,
         clicking a colour variant thumb caused the overlay to be
         force-hidden, exposing the bare mug between transitions. */
      if (activeThumb.hasAttribute('data-variant-thumb')) return true;
      return activeThumb === thumbs[0];
    }

    function sync() {
      var show = isPrimaryShowing();
      var size = getActivePickerValue(root, 'size');
      var colour = getActivePickerValue(root, 'colour');

      // Re-apply size-specific hero when user returns to primary thumb —
      // the sipfull thumb-click handler will have reset hero.src to the
      // ceramic photo, overriding our swap.
      if (show) {
        var heroUrl = pickHeroUrlForSize(heroOverlays[0], size);
        if (heroUrl) setHeroSrc(heroImg, heroUrl);
      }

      // Show overlay only if (a) the primary thumb is active AND (b) the
      // current size+colour combination should have an overlay. Without
      // (b), an overlay activated under a previous state (e.g. Red) would
      // linger when transitioning to Travel Mug+Black or Latte, where no
      // overlay should be visible.
      heroOverlays.forEach(function (overlay) {
        if (!overlay.hasAttribute('data-activated')) return;
        var validForState = pickOverlayUrl(overlay, colour, size) !== null;
        overlay.style.display = (show && validForState) ? '' : 'none';
      });
    }

    new MutationObserver(sync).observe(heroImg, {
      attributes: true,
      attributeFilter: ['src', 'hidden']
    });
    root.querySelectorAll('[data-sp-thumb]').forEach(function (t) {
      new MutationObserver(sync).observe(t, {
        attributes: true,
        attributeFilter: ['class']
      });
    });
  }

  // ---- gallery hero load gate -------------------------------------------
  //
  // Without this, the colour overlay <img> can paint before the underlying
  // hero photo has finished loading — customers briefly see the overlay
  // floating on a blank/placeholder image. The gate hides BOTH the hero
  // and the overlay until every visible image in the hero is loaded, then
  // reveals them together via an `is-ready` class that the CSS uses to
  // fade them in. A loading shimmer keeps the area visually busy in the
  // meantime so the wait doesn't feel like a broken page.
  function isElementVisible(el, stopAt) {
    var node = el;
    while (node && node !== stopAt) {
      if (node.nodeType === 1) {
        var cs = window.getComputedStyle(node);
        if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      }
      node = node.parentElement;
    }
    return true;
  }

  function gateGalleryHeroReveal(galleryEl) {
    if (!galleryEl || galleryEl.classList.contains('is-ready')) return;

    // Personalised heroes render the design into a <canvas> (the slow part
    // is the large design webp decoding), which the <img> checks below
    // can't observe. So we also wait for the engine's first-paint signal
    // and reveal the thumbnail strip alongside the hero — nothing shows
    // until the whole thing is ready.
    var isPersonalised = galleryEl.classList.contains('sp-gallery-hero--personalised');
    var galleryRoot = galleryEl.closest('.sp-gallery');
    var thumbsWrap = galleryRoot ? galleryRoot.querySelector('.sp-thumbs-wrap') : null;

    function reveal() {
      if (galleryEl.classList.contains('is-ready')) return;
      galleryEl.classList.add('is-ready');
      if (isPersonalised && thumbsWrap) thumbsWrap.classList.add('is-ready');
    }

    // Collect every <img> currently visible in the hero (the standard
    // hero img, the colour overlay if shown, and the per-pane images
    // for personalised galleries — only the active pane is visible).
    var imgs = Array.prototype.slice.call(
      galleryEl.querySelectorAll('img')
    ).filter(function (img) {
      // Skip imgs without a usable src (overlay starts empty for sizes
      // where no overlay applies — Latte, Travel Mug non-white).
      var src = img.getAttribute('src');
      if (!src) return false;
      // Skip imgs hidden by ancestor display:none (other view panes,
      // hidden colour overlays).
      return isElementVisible(img, galleryEl);
    });

    var pending = 0;
    function maybeReady() {
      pending--;
      if (pending <= 0) reveal();
    }

    imgs.forEach(function (img) {
      // Already loaded from cache → counts as ready.
      if (img.complete && img.naturalWidth > 0) return;
      pending++;
      img.addEventListener('load',  maybeReady, { once: true });
      img.addEventListener('error', maybeReady, { once: true });
    });

    // Wait for the personaliser engine's first full paint (design decoded +
    // every view rendered). If it already fired, no need to wait.
    if (isPersonalised && !window.fbPersonaliserReady) {
      pending++;
      document.addEventListener('fb-personaliser:ready', maybeReady, { once: true });
    }

    if (pending === 0) {
      reveal();
      return;
    }

    // Safety net — never let the loader stay forever. If something's
    // genuinely broken (e.g. blocked image / engine error), reveal anyway
    // so the page is usable. Personalised waits a little longer for the
    // design webp to download on slow connections.
    setTimeout(reveal, isPersonalised ? 8000 : 4000);
  }

  function boot() {
    document.querySelectorAll('[data-sipfull-product]').forEach(function (root) {
      initSipfullPicker(root);
      initSipfullHeroSrcWatcher(root);
    });
    document.querySelectorAll('.sp-gallery-hero').forEach(gateGalleryHeroReveal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
